import { booleanAttribute, ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { IrdIconRegistryService } from '../../core/icon-registry/ird-icon-registry.service';

export type IrdIconSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type IrdIconColor =
  // Not a CP-Icon variant: takes the colour of whatever contains it, for an icon whose colour is
  // owned by its container — e.g. projected into an ird-button, where the button variant decides it.
  | 'inherit'
  | 'default'
  | 'default-light'
  | 'default-disabled'
  | 'inverse'
  | 'inverse-light'
  | 'inverse-disabled'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'branded';

@Component({
  selector: 'ird-icon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ird-icon.component.html',
  styleUrl: './ird-icon.component.scss',
  host: {
    role: 'img',
    '[class]': 'hostClasses()',
    '[attr.aria-label]': 'ariaLabel()',
    '[attr.aria-hidden]': '!ariaLabel() ? "true" : null',
  },
})
export class IrdIconComponent {
  private readonly registry = inject(IrdIconRegistryService);
  private readonly sanitizer = inject(DomSanitizer);

  /** Icon name — when registered in the registry, renders the SVG automatically. */
  readonly name = input<string>();

  /** Size variant mapped to contextual design tokens. */
  readonly size = input<IrdIconSize>('md');

  /** Color variant mapped to contextual design tokens. */
  readonly color = input<IrdIconColor>('default');

  /**
   * Opt in for fill-drawn icons (e.g. shared icon `wrench`). By default only the stroke
   * follows `color`, because forcing a fill would flood stroke-drawn icons into solid blobs.
   */
  readonly filled = input(false, { transform: booleanAttribute });

  /**
   * Puts the icon on a rounded square tinted to match `color` (Figma `Background=Filled`), e.g. for
   * success, warning and error results. The padding grows the box; the icon itself keeps its size.
   */
  readonly background = input(false, { transform: booleanAttribute });

  /** Explicit accessible label. Falls back to `name` if provided. */
  readonly ariaLabel = input<string | undefined>(undefined, { alias: 'aria-label' });

  /** @internal Trusted SVG HTML from the registry (if icon name is registered). */
  readonly svgHtml = computed(() => {
    const iconName = this.name();
    // Read version to make this reactive to new registrations
    this.registry.version();
    if (!iconName) {
      return null;
    }
    const data = this.registry.get(iconName);
    return data ? this.sanitizer.bypassSecurityTrustHtml(data) : null;
  });

  /** @internal */
  readonly hostClasses = computed(
    () =>
      `icon ird-icon ird-icon--${this.size()} ird-icon--${this.color()}` +
      `${this.filled() ? ' ird-icon--filled' : ''}${this.background() ? ' ird-icon--background' : ''}`,
  );
}
